import React, { useState } from 'react';
import { Layers, Download, Info, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика получения налоговых вычетов
 */
const DeductionsSchedule = ({ 
  schedule, 
  results, 
  formData,
  showFullSchedule, 
  setShowFullSchedule 
}) => {
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первых трех лет, если не показан полный график
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, 3);

  // Итоговые суммы
  const totalTaxReturn = schedule.reduce((sum, item) => sum + item.taxReturn, 0);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-emerald-500" />
          График получения налоговых вычетов
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 3 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${schedule.length} лет`}
          </Button>
        )}
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showFullSchedule 
            ? "Полный график возврата НДФЛ по годам. Учитывается ограничение по уплаченному НДФЛ за год." 
            : `Показаны первые ${displaySchedule.length} года получения вычета. Нажмите "Показать все годы" для просмотра полного графика.`
          }</p>
        </div>
      </div>

      {/* Контейнер таблицы */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 deductions-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold">Год</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">Возврат НДФЛ</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">Остаток к возврату</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">Годовой НДФЛ</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">% использования</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displaySchedule.map((item, index) => {
              // Процент использования годового НДФЛ
              const utilizationPercent = (item.taxReturn / item.annualIncomeTax) * 100;
              
              return (
                <tr
                  key={item.year}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
                >
                  <td className="px-4 py-3 whitespace-nowrap font-medium">
                    {item.year}
                    {index === 0 && <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">(текущий)</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right font-medium text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(item.taxReturn)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    {formatCurrency(item.remainingReturn)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    {formatCurrency(item.annualIncomeTax)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end">
                      <span className="mr-2">{Math.round(utilizationPercent)}%</span>
                      <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                        <div 
                          className={`h-2.5 rounded-full ${
                            utilizationPercent >= 95 
                              ? 'bg-emerald-500 dark:bg-emerald-400' 
                              : utilizationPercent >= 60 
                                ? 'bg-yellow-500 dark:bg-yellow-400' 
                                : 'bg-blue-500 dark:bg-blue-400'
                          }`}
                          style={{ width: `${Math.min(100, utilizationPercent)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Итоговая строка */}
          <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold text-gray-800 dark:text-gray-200">
            <tr>
              <td className="px-4 py-3 whitespace-nowrap">ИТОГО:</td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-emerald-600 dark:text-emerald-400">
                {formatCurrency(totalTaxReturn)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">-</td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                {formatCurrency(results.totalAnnualTaxPaid * schedule.length)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                {formatNumber((totalTaxReturn / (results.totalAnnualTaxPaid * schedule.length)) * 100, 0)}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      
      {/* Визуальный график полученных вычетов */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Распределение возврата НДФЛ по годам:
        </h4>
        <div className="space-y-3">
          {displaySchedule.map((item) => {
            // Процент от общей суммы вычета
            const percentOfTotal = (item.taxReturn / totalTaxReturn) * 100;
            
            return (
              <div key={item.year} className="space-y-1">
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>{item.year} год</span>
                  <span>{formatCurrency(item.taxReturn)} ({Math.round(percentOfTotal)}%)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full ${
                      item.year === results.startYear 
                        ? 'bg-emerald-500 dark:bg-emerald-400' 
                        : 'bg-blue-500 dark:bg-blue-400'
                    }`}
                    style={{ width: `${Math.max(4, percentOfTotal)}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > 3 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} лет получения вычета
          </Button>
        </div>
      )}

      {/* Блок с советами */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
          <Info className="w-4 h-4 mr-2 text-emerald-500" />
          Советы по получению вычета:
        </h4>
        <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400 list-disc ml-5">
          <li>Подавайте документы сразу после окончания календарного года, чтобы быстрее получить возврат</li>
          <li>Для имущественного вычета можно получить уведомление и не платить НДФЛ с зарплаты у работодателя</li>
          <li>Рассмотрите подачу документов через личный кабинет налогоплательщика - это ускорит процесс</li>
          <li>Социальные вычеты (лечение, обучение) не переносятся на следующие годы, заявляйте их своевременно</li>
          {formData.deductionType === 'property' && (
            <li>Супруги могут распределить имущественный вычет между собой для более быстрого получения</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default DeductionsSchedule;